import * as Sentry from '@sentry/nuxt';
import { useSessionStorage } from '@vueuse/core';

export default defineNuxtPlugin(nuxtApp => {
  const router = useRouter();

  // useSessionStorage handles SecurityError internally (private mode, third-party
  // iframe contexts, "block all cookies" etc.) — failures route through onError
  // instead of throwing. Worst case we forfeit loop protection (rare double
  // reload) but the reload itself still happens.
  const lastReload = useSessionStorage<number>('nuxt-reload-protection', 0);

  // --- HELPER: RELOAD WITH LOOP PROTECTION ---
  const reloadWithProtection = (source: string, error: unknown) => {
    const now = Date.now();
    const message = error instanceof Error ? error.message : String(error);

    // 1. Loop Protection: If we reloaded less than 10 seconds ago, STOP.
    if (now - lastReload.value < 10000) {
      console.error('Reload loop detected. Stopping auto-refresh to prevent browser crash.');
      // Sentry filter drops the raw chunk-load errors; capture this so we
      // can still see when reload didn't recover the user.
      Sentry.captureMessage('chunk-reload loop protection tripped', {
        level: 'warning',
        tags: { source },
        extra: { message, msSinceLastReload: now - lastReload.value },
      });
      return;
    }

    // 2. Mark this reload timestamp
    lastReload.value = now;

    // 3. Breadcrumb so we can trace recoveries on any subsequent error.
    Sentry.addBreadcrumb({
      category: 'chunk-reload',
      level: 'info',
      message: `Reloading after chunk load error (${source})`,
      data: { message },
    });

    // 4. Reload
    console.warn('Chunk load error detected. Reloading to attempt recovery...');
    window.location.reload();
  };

  // --- 1. HOOK: GLOBAL APP ERROR ---
  nuxtApp.hook('app:error', (error: any) => {
    const message = (error?.message || '').toLowerCase(); // Lowercase for safer matching

    // Check for all known variations of chunk errors
    const isChunkLoadError =
      message.includes('loading chunk') ||
      message.includes('importing a module script failed') ||
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('error loading dynamically imported module'); // <--- ADDED THIS

    if (isChunkLoadError) {
      reloadWithProtection('app:error', error);
    }
  });

  // --- 2. HOOK: ROUTER ERROR ---
  router.onError((error: any) => {
    const message = (error?.message || '').toLowerCase();

    const isChunkLoadError =
      message.includes('failed to fetch dynamically imported module') ||
      message.includes('importing a module script failed') ||
      message.includes('error loading dynamically imported module'); // <--- ADDED THIS

    if (isChunkLoadError) {
      console.warn('Router navigation failed due to version mismatch.');
      reloadWithProtection('router.onError', error);
    }
  });
});
